import type { QualityBreakdownItem } from '../types'
import { QUALITY_COLORS } from '../types'

interface QualityDonutChartProps {
  breakdown: QualityBreakdownItem[]
  totalPaidCalls: number
  totalCalls?: number
}

export function QualityDonutChart({ breakdown, totalPaidCalls, totalCalls }: QualityDonutChartProps) {
  const displayTotal = totalCalls ?? totalPaidCalls
  // Calculate SVG path for donut segments
  const size = 160
  const strokeWidth = 32
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const center = size / 2

  // Calculate segment offsets
  let cumulativePercentage = 0
  const segments = breakdown.map((item) => {
    const dashLength = (item.percentage / 100) * circumference
    const dashOffset = circumference - (cumulativePercentage / 100) * circumference
    cumulativePercentage += item.percentage
    return {
      ...item,
      dashArray: `${dashLength} ${circumference - dashLength}`,
      dashOffset,
      colorHex: QUALITY_COLORS[item.color] || QUALITY_COLORS.gray,
    }
  })

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h3 className="text-sm font-medium text-gray-500 mb-4">Call Quality Breakdown</h3>

      <div className="flex items-center gap-6">
        {/* Donut Chart */}
        <div className="relative flex-shrink-0">
          <svg width={size} height={size} className="transform -rotate-90">
            {/* Background circle */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={strokeWidth}
            />
            {/* Segments */}
            {segments.map((segment, index) => (
              <circle
                key={segment.label}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={segment.colorHex}
                strokeWidth={strokeWidth}
                strokeDasharray={segment.dashArray}
                strokeDashoffset={segment.dashOffset}
                className="transition-all duration-300"
              />
            ))}
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-gray-900">{displayTotal}</span>
            <span className="text-xs text-gray-500">Total Calls</span>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-2">
          {breakdown.map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: QUALITY_COLORS[item.color] }}
                />
                <span className="text-sm text-gray-600">{item.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">{item.value}</span>
                <span className="text-xs text-gray-500">({item.percentage}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
